import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";
import { productBySlug, getProductBasePrice, type Bundle, type Country } from "./catalog";
import { useCurrency } from "./currency";
import { lookupAffiliateByCode } from "@/lib/affiliate.functions";
import { lookupCouponByCode } from "@/lib/coupon.functions";
import { mirrorCart } from "@/lib/cart.functions";

const CART_KEY = "banana.cart.v1";
const CART_TOKEN_COOKIE = "banana_cart_token";

export type CartItem = { slug: string; bundleQty: number; qty: number };
type PersistedState = { items: CartItem[]; couponCode?: string | null };

function readCookie(n: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + n + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}
function writeCookie(n: string, v: string) {
  if (typeof document === "undefined") return;
  document.cookie = `${n}=${encodeURIComponent(v)}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
}
function ensureToken(): string {
  let t = readCookie(CART_TOKEN_COOKIE);
  if (!t) {
    t = typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
    writeCookie(CART_TOKEN_COOKIE, t);
  }
  return t;
}

function bundleFor(slug: string, qty: number): Bundle | undefined {
  const p = productBySlug(slug);
  if (!p) return undefined;
  return p.bundles.find((b) => b.qty === qty) ?? p.bundles[0];
}

export function lineFor(item: CartItem, country: Country) {
  const product = productBySlug(item.slug);
  if (!product) return null;
  const base = getProductBasePrice(item.slug, country.code);
  const b = bundleFor(item.slug, item.bundleQty);
  if (!b) return null;
  const unitPrice = base * b.perCard;
  const lineQty = b.qty * item.qty;
  return {
    product,
    bundle: b,
    unitPrice,
    lineQty,
    lineTotal: unitPrice * lineQty,
    lineCompareAt: base * lineQty,
  };
}

export type Coupon = {
  code: string;
  type: "percent" | "fixed";
  value: number;
  label?: string | null;
};

type Ctx = {
  items: CartItem[];
  count: number;
  subtotal: number;
  compareAt: number;
  bundleDiscount: number;
  couponDiscount: number;
  coupon: Coupon | null;
  total: number;
  open: boolean;
  setOpen: (v: boolean) => void;
  addItem: (slug: string, bundleQty: number, qty?: number) => void;
  removeItem: (index: number) => void;
  updateQty: (index: number, qty: number) => void;
  clear: () => void;
  applyCoupon: (code: string) => Promise<{ ok: boolean; message?: string }>;
  removeCoupon: () => void;
};

const CartCtx = createContext<Ctx | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const { country } = useCurrency();
  const [items, setItems] = useState<CartItem[]>([]);
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [open, setOpen] = useState(false);
  const tokenRef = useRef<string | null>(null);
  const mirrorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hydrated = useRef(false);

  // hydrate from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(CART_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as PersistedState;
        if (Array.isArray(parsed.items)) setItems(parsed.items);
      }
    } catch {}
    tokenRef.current = ensureToken();
    hydrated.current = true;
  }, []);

  // persist locally + debounced mirror to supabase (fire & forget)
  useEffect(() => {
    if (!hydrated.current) return;
    const state: PersistedState = { items, couponCode: coupon?.code ?? null };
    try { localStorage.setItem(CART_KEY, JSON.stringify(state)); } catch {}
    if (mirrorTimer.current) clearTimeout(mirrorTimer.current);
    mirrorTimer.current = setTimeout(() => {
      const token = tokenRef.current;
      if (!token) return;
      try {
        void mirrorCart({
          data: {
            token,
            country: country.code,
            currency: country.currency,
            data: { items: state.items, couponCode: state.couponCode ?? null },
          },
        }).catch(() => {});
      } catch {
        // swallow: cart UX must not depend on network
      }
    }, 700);
  }, [items, coupon, country]);

  const addItem = useCallback((slug: string, bundleQty: number, qty = 1) => {
    setItems((prev) => {
      const i = prev.findIndex((x) => x.slug === slug && x.bundleQty === bundleQty);
      if (i >= 0) {
        const copy = [...prev];
        copy[i] = { ...copy[i], qty: copy[i].qty + qty };
        return copy;
      }
      return [...prev, { slug, bundleQty, qty }];
    });
    setOpen(true);
  }, []);
  const removeItem = useCallback((i: number) => setItems((prev) => prev.filter((_, idx) => idx !== i)), []);
  const updateQty = useCallback((i: number, qty: number) => {
    setItems((prev) => prev.map((x, idx) => (idx === i ? { ...x, qty: Math.max(1, qty) } : x)));
  }, []);
  const clear = useCallback(() => { setItems([]); setCoupon(null); }, []);

  const applyCoupon = useCallback(async (code: string): Promise<{ ok: boolean; message?: string }> => {
    const c = code.trim().toUpperCase();
    if (!c) return { ok: false, message: "Enter a coupon code" };
    try {
      const data = await lookupCouponByCode({ data: { code: c } });
      if (!data) {
        // Fall back to affiliate discount codes managed in the Partners project.
        try {
          const aff = await lookupAffiliateByCode({ data: { code: c } });
          if (aff && (aff.discount_code || aff.affiliate_link_code)) {
            setCoupon({
              code: (aff.discount_code || aff.affiliate_link_code || c).toUpperCase(),
              type: "percent",
              value: 10,
              label: "Affiliate discount",
            });
            return { ok: true };
          }
        } catch {
          // ignore, fall through to invalid coupon
        }
        return { ok: false, message: "Invalid coupon" };
      }
      const row = data;
      if (!row.active) return { ok: false, message: "Coupon inactive" };
      const now = new Date();
      if (row.starts_at && new Date(row.starts_at) > now) return { ok: false, message: "Coupon not active yet" };
      if (row.ends_at && new Date(row.ends_at) < now) return { ok: false, message: "Coupon expired" };
      if (row.max_uses && (row.times_used ?? 0) >= row.max_uses) return { ok: false, message: "Coupon limit reached" };
      setCoupon({
        code: row.code,
        type: row.discount_type === "fixed" ? "fixed" : "percent",
        value: Number(row.discount_value),
        label: row.label,
      });
      return { ok: true };
    } catch {
      return { ok: false, message: "Could not validate coupon" };
    }
  }, []);

  const removeCoupon = useCallback(() => setCoupon(null), []);

  const totals = useMemo(() => {
    let subtotal = 0;
    let compareAt = 0;
    let count = 0;
    for (const it of items) {
      const li = lineFor(it, country);
      if (!li) continue;
      subtotal += li.lineTotal;
      compareAt += li.lineCompareAt;
      count += li.lineQty;
    }
    const bundleDiscount = compareAt - subtotal;
    const couponDiscount = !coupon
      ? 0
      : coupon.type === "percent"
      ? subtotal * (coupon.value / 100)
      : Math.min(subtotal, coupon.value);
    const total = Math.max(0, subtotal - couponDiscount);
    return { subtotal, compareAt, bundleDiscount, couponDiscount, total, count };
  }, [items, coupon, country]);

  const value: Ctx = {
    items, ...totals, coupon, open, setOpen,
    addItem, removeItem, updateQty, clear, applyCoupon, removeCoupon,
  };
  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function useCart() {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}